/**
 * =============================================================================
 * SPEQ55 RFP Pro - Usage Meter Component
 * =============================================================================
 *
 * Displays a visual progress bar showing the user's monthly RFP analysis usage.
 * Color-coded to indicate usage level:
 * - Green: 0-70% used
 * - Yellow: 70-90% used
 * - Red: 90-100% used
 *
 * @module components/usage/UsageMeter
 * @author SPEQ55
 */

import PropTypes from 'prop-types';

/**
 * UsageMeter component displays usage statistics with a visual progress bar.
 *
 * @param {Object} props - Component props
 * @param {number} props.count - Current usage count
 * @param {number} props.limit - Monthly limit
 * @param {string} props.resetsAt - ISO date string when usage resets
 * @param {string} [props.className] - Additional CSS classes
 */
const UsageMeter = ({ count, limit, resetsAt, className = '' }) => {
  const percentUsed = limit > 0 ? Math.round((count / limit) * 100) : 0;

  // Determine color based on usage percentage
  const getColorClass = () => {
    if (percentUsed >= 90) return 'bg-red-500';
    if (percentUsed >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTextColorClass = () => {
    if (percentUsed >= 90) return 'text-red-700';
    if (percentUsed >= 70) return 'text-yellow-700';
    return 'text-green-700';
  };

  const getBgColorClass = () => {
    if (percentUsed >= 90) return 'bg-red-50';
    if (percentUsed >= 70) return 'bg-yellow-50';
    return 'bg-green-50';
  };

  // Format reset date
  const formatResetDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={`rounded-lg border p-4 ${getBgColorClass()} ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Monthly Analysis Usage</h3>
        <span className={`text-sm font-bold ${getTextColorClass()}`}>
          {count} / {limit}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${getColorClass()}`}
          style={{ width: `${Math.min(percentUsed, 100)}%` }}
        />
      </div>

      {/* Usage Details */}
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>{percentUsed}% used</span>
        <span>Resets {formatResetDate(resetsAt)}</span>
      </div>

      {/* Warning Message */}
      {percentUsed >= 90 && (
        <div className="mt-3 text-xs text-red-700 font-medium">
          ⚠️ You're approaching your monthly limit. Contact your administrator for additional
          analyses.
        </div>
      )}
      {percentUsed >= 70 && percentUsed < 90 && (
        <div className="mt-3 text-xs text-yellow-700 font-medium">
          ⚠️ You've used {percentUsed}% of your monthly analyses.
        </div>
      )}
    </div>
  );
};

UsageMeter.propTypes = {
  count: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  resetsAt: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default UsageMeter;
